import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import apiClient from '../utils/api';

// n-of-1 experiments: try one change, see honestly what moved. Single-subject —
// the user is their own control. One wearable outcome, one behavioural change,
// a before/after read behind a coverage + noise gate. "During X, your Y moved" —
// association, never causation.

const BRAND = {
  before: '#B8860B',   // muted gold — the baseline "before" window
  moved_good: '#10B981',
  moved_bad: '#EF4444',
  neutral: '#FFB800',  // gold — notable but not judged good/bad
  grid: 'rgba(148,163,184,0.18)',
};

const DURATIONS = [
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
  { days: 21, label: '3 weeks' },
  { days: 28, label: '4 weeks' },
];

// The colour that carries a result's meaning (green good / red bad / gold notable).
const resultColor = (r) => {
  if (r.verdict === 'moved') {
    if (r.favorable === true) return BRAND.moved_good;
    if (r.favorable === false) return BRAND.moved_bad;
    return BRAND.neutral;
  }
  return BRAND.neutral;
};

const fmtDate = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Verdict chip
// ---------------------------------------------------------------------------

const VerdictChip = ({ result }) => {
  const map = {
    moved: { text: 'Something moved', cls: '' },
    no_change: { text: 'No clear change', cls: 'bg-elev text-fg-2  ' },
    inconclusive: {
      text: result.complete ? 'Set aside' : 'In progress',
      cls: 'bg-elev text-fg-2  ',
    },
  };
  const m = map[result.verdict] || map.inconclusive;
  const style = result.verdict === 'moved'
    ? { backgroundColor: `${resultColor(result)}22`, color: resultColor(result) }
    : undefined;
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-full text-sm font-medium ${m.cls}`}
      style={style}
    >
      {m.text}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Before / during chart
// ---------------------------------------------------------------------------

const ExperimentChart = ({ exp }) => {
  const { result } = exp;
  const data = useMemo(() => (result.series || []).map((p) => ({
    date: p.date,
    before: p.phase === 'baseline' ? p.value : null,
    during: p.phase === 'experiment' ? p.value : null,
  })), [result.series]);

  if (!data.length) {
    return (
      <div className="h-56 flex items-center justify-center text-fg-3 text-sm">
        No {exp.outcome_label.toLowerCase()} readings yet — they'll appear as your wearable syncs.
      </div>
    );
  }
  const during = resultColor(result);
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid stroke={BRAND.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 12 }}
                 minTickGap={24} stroke="#94a3b8" />
          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} width={44}
                 stroke="#94a3b8" unit={exp.unit === '%' ? '%' : ''} />
          <Tooltip
            labelFormatter={fmtDate}
            formatter={(v, name) => [v, name === 'before' ? 'Before' : 'During']}
            contentStyle={{ fontSize: 13, borderRadius: 8 }}
          />
          {result.baseline_mean != null && (
            <ReferenceLine y={result.baseline_mean} stroke={BRAND.before}
                           strokeDasharray="4 4" strokeWidth={1.5} />
          )}
          {result.experiment_mean != null && (
            <ReferenceLine y={result.experiment_mean} stroke={during}
                           strokeDasharray="4 4" strokeWidth={1.5} />
          )}
          <Line type="monotone" dataKey="before" stroke={BRAND.before} strokeWidth={2}
                dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="during" stroke={during} strokeWidth={2.5}
                dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex gap-4 justify-center mt-1 text-sm text-fg-2">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 rounded" style={{ background: BRAND.before }} /> Before
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 rounded" style={{ background: during }} /> During
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The result read (shared by active + past)
// ---------------------------------------------------------------------------

const ResultRead = ({ exp }) => {
  const { result } = exp;
  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-1.5">
        <VerdictChip result={result} />
        {!result.complete && exp.status === 'active' && (
          <span className="text-sm text-fg-3">
            Day {result.days_elapsed} of {result.days_total}
          </span>
        )}
      </div>
      <p className="text-base font-medium text-fg">{result.headline}</p>
      <p className="text-base text-fg-2 mt-1">{result.note}</p>
      {result.caveat && (
        <p className="text-sm text-fg-3 mt-2 italic">{result.caveat}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Active experiment
// ---------------------------------------------------------------------------

const ActiveExperiment = ({ exp, onChange }) => {
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === 'abandon' &&
        !window.confirm('Discard this experiment? Its result won’t be kept.')) return;
    setBusy(true);
    try {
      await apiClient.patch(`/api/experiments/${exp.id}`, { action });
      toast.success(action === 'end_now' ? 'Experiment ended.' : 'Experiment discarded.');
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the experiment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="text-sm font-medium text-fg-2 uppercase tracking-wide">
          Running now
        </div>
        <CardTitle className="text-xl">{exp.name}</CardTitle>
        <p className="text-sm text-fg-3">
          Watching your {exp.outcome_label.toLowerCase()} · {fmtDate(exp.start_date)} → {fmtDate(exp.end_date)}
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <ResultRead exp={exp} />
        <ExperimentChart exp={exp} />
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={() => act('end_now')} disabled={busy}>
            End now &amp; keep result
          </Button>
          <button
            onClick={() => act('abandon')}
            disabled={busy}
            className="text-sm text-fg-3 hover:text-off-strong  dark:hover:text-red-400 font-medium"
          >
            Discard
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

const CreateExperiment = ({ outcomes, onCreated }) => {
  const [name, setName] = useState('');
  const [metric, setMetric] = useState('');
  const [duration, setDuration] = useState(14);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!metric && outcomes.length) setMetric(outcomes[0].metric_key);
  }, [outcomes, metric]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Give your experiment a name.'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/experiments', {
        name: name.trim(), outcome_metric: metric, duration_days: duration,
      });
      toast.success('Experiment started.');
      setName('');
      onCreated();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not start the experiment.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-line ' +
    'bg-white dark:bg-black/40 px-3 py-2.5 text-base text-fg ' +
    'focus:outline-none focus:ring-2 focus:ring-fg-3';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Start an experiment</CardTitle>
        <p className="text-base text-fg-2 mt-1">
          Change one thing. We'll compare the next stretch against the same stretch
          just before it, using your wearable data — so there's nothing extra to log.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1.5">
              What are you changing?
            </label>
            <input
              className={inputCls}
              placeholder="e.g. Magnesium before bed"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1.5">
              What should we watch?
            </label>
            <select className={inputCls} value={metric} onChange={(e) => setMetric(e.target.value)}>
              {outcomes.map((o) => (
                <option key={o.metric_key} value={o.metric_key}>{o.label}</option>
              ))}
            </select>
            <p className="text-sm text-fg-3 mt-1.5">
              Measured automatically by your wearable — your logging habits won't affect the result.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1.5">
              For how long?
            </label>
            <div className="flex flex-wrap gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d.days}
                  type="button"
                  onClick={() => setDuration(d.days)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    duration === d.days
                      ? 'bg-cta text-cta-fg border-fg-3'
                      : 'border-line text-fg-2'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Starting…' : 'Start experiment'}
          </Button>
          <p className="text-sm text-fg-3 text-center">
            One experiment at a time. It'll read your last {duration} days as the "before".
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Past experiments
// ---------------------------------------------------------------------------

const PastExperiment = ({ exp }) => {
  const [open, setOpen] = useState(false);
  const abandoned = exp.status === 'abandoned';
  return (
    <Card>
      <button
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="min-w-0">
          <p className="text-base font-medium text-fg truncate">{exp.name}</p>
          <p className="text-sm text-fg-3">
            {exp.outcome_label} · {fmtDate(exp.start_date)} → {fmtDate(exp.end_date)}
          </p>
        </div>
        {abandoned
          ? <span className="text-sm text-fg-3">Discarded</span>
          : <VerdictChip result={exp.result} />}
      </button>
      {open && !abandoned && (
        <CardContent className="space-y-4 border-t border-line pt-4">
          <ResultRead exp={exp} />
          <ExperimentChart exp={exp} />
        </CardContent>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// A proposed experiment — the engine asking to test something properly (N9)
//
// Sits above the create form rather than replacing it: the engine suggesting
// something must never be the only way in. And it's one card, never a list,
// because you can only run one experiment at a time — showing three would be
// showing two you have to refuse.
// ---------------------------------------------------------------------------

const Proposal = ({ proposal, onAnswered }) => {
  const [busy, setBusy] = useState(false);

  const answer = async (accept) => {
    setBusy(true);
    try {
      await apiClient.post(`/api/experiments/proposal/${proposal.id}/${accept ? 'accept' : 'decline'}`);
      await onAnswered();
      if (accept) toast.success('Experiment started.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not do that just now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-l-4 border-l-primary-500">
      <CardContent className="p-5 space-y-3">
        <p className="text-base font-medium text-fg">
          {proposal.headline}
        </p>
        {proposal.rationale && (
          <p className="text-base text-fg-2 leading-relaxed">
            {proposal.rationale}
          </p>
        )}
        <p className="text-sm text-fg-2 leading-relaxed">
          {proposal.basis_note}
        </p>
        <p className="text-sm text-fg-2 leading-relaxed">
          {proposal.caveat}
        </p>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => answer(true)}
            className="flex-1 rounded-lg bg-cta px-3 py-2 text-base font-medium text-cta-fg disabled:opacity-50"
          >
            Run it
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => answer(false)}
            className="flex-1 rounded-lg border border-line px-3 py-2 text-base font-medium text-fg disabled:opacity-50"
          >
            Not this one
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ExperimentsPage = () => {
  const [outcomes, setOutcomes] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [oRes, eRes, pRes] = await Promise.all([
        apiClient.get('/api/experiments/outcomes'),
        apiClient.get('/api/experiments'),
        // A failed proposal fetch must not take the page down with it — the
        // page's job is running experiments, and the suggestion is a bonus.
        apiClient.get('/api/experiments/proposal').catch(() => ({ data: {} })),
      ]);
      setOutcomes(oRes.data || []);
      setExperiments(eRes.data || []);
      setProposal(pRes.data?.proposal || null);
    } catch (e) {
      toast.error('Could not load your experiments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = experiments.find((e) => e.status === 'active');
  const past = experiments.filter((e) => e.status !== 'active');

  return (
    <div className="max-w-2xl mx-auto px-4 pt-2 pb-10 space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-fg">Experiments</h1>
        <p className="text-base text-fg-2 mt-1">
          Try one change and see honestly what moved — measured on you, against you.
        </p>
      </header>

      {loading ? (
        <div className="py-16 text-center text-fg-3">Loading…</div>
      ) : active ? (
        <ActiveExperiment exp={active} onChange={load} />
      ) : (
        <>
          {proposal && <Proposal proposal={proposal} onAnswered={load} />}
          <CreateExperiment outcomes={outcomes} onCreated={load} />
        </>
      )}

      {past.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-fg-3 uppercase tracking-wide">
            Past experiments
          </h2>
          {past.map((e) => <PastExperiment key={e.id} exp={e} />)}
        </section>
      )}
    </div>
  );
};

export default ExperimentsPage;
